/**
 * Assignment API Client
 */

import { apiGet, apiGetPaginated, apiPatch, apiPost, apiPut } from '@/services/api/baseService';

// ─── Types ─────────────────────────────────────────────────────────

export interface AssignmentCategory {
  id: number;
  code: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  is_active: boolean;
}

export interface AssignmentListItem {
  id: number;
  organization_id: string;
  organization_name: string;
  category_id: number;
  category_code: string;
  category_name: string;
  category_icon: string | null;
  category_color: string | null;
  vehicle_id: number | null;
  registration_number: string | null;
  title: string;
  description: string | null;
  assigned_by: string;
  assigned_by_name: string;
  assigned_to: string;
  assigned_to_name: string;
  assigned_at: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  started_at: string | null;
  completed_at: string | null;
  completion_notes: string | null;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  due_date: string | null;
  created_at: string;
}

export interface CreateAssignmentData {
  category_id: number;
  vehicle_id?: number | null;
  title: string;
  description?: string;
  assigned_to: string;
  priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  due_date?: string;
}

export interface UpdateAssignmentData {
  category_id?: number;
  vehicle_id?: number | null;
  title?: string;
  description?: string;
  assigned_to?: string;
  priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  due_date?: string | null;
}

interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ─── API Functions ─────────────────────────────────────────────────

export const assignmentApi = {
  async getAssignments(params?: Record<string, string | number>): Promise<{ data: AssignmentListItem[]; pagination: PaginationInfo }> {
    return apiGetPaginated<AssignmentListItem>('/api/assignments', { params });
  },

  async getMyAssignments(includeCompleted = false): Promise<AssignmentListItem[]> {
    return apiGet<AssignmentListItem[]>('/api/assignments/my', { params: { includeCompleted } });
  },

  async getById(id: number): Promise<AssignmentListItem> {
    return apiGet<AssignmentListItem>(`/api/assignments/${id}`);
  },

  async create(data: CreateAssignmentData): Promise<AssignmentListItem> {
    return apiPost<AssignmentListItem>('/api/assignments', data);
  },

  async update(id: number, data: UpdateAssignmentData): Promise<AssignmentListItem> {
    return apiPut<AssignmentListItem>(`/api/assignments/${id}`, data);
  },

  async start(id: number): Promise<AssignmentListItem> {
    return apiPatch<AssignmentListItem>(`/api/assignments/${id}/start`);
  },

  async complete(id: number, completionNotes?: string): Promise<AssignmentListItem> {
    return apiPatch<AssignmentListItem>(`/api/assignments/${id}/complete`, { completion_notes: completionNotes });
  },

  async cancel(id: number): Promise<AssignmentListItem> {
    return apiPatch<AssignmentListItem>(`/api/assignments/${id}/cancel`);
  },

  async bulkCreate(data: {
    items: Array<{ vehicle_id: number; title: string; registration_number?: string }>;
    category_id: number;
    assigned_to: string;
    priority?: string;
  }): Promise<{ created: number }> {
    return apiPost<{ created: number }>('/api/assignments/bulk', data);
  },

  async getOpenByVehicleIds(
    vehicleIds: number[],
    categoryCode?: string,
  ): Promise<Record<number, { assignmentId: number; assignedToName: string; status: string }>> {
    return apiGet('/api/assignments/by-vehicles', {
      params: {
        vehicleIds: vehicleIds.join(','),
        ...(categoryCode ? { categoryCode } : {}),
      },
    });
  },

  async getCategories(): Promise<AssignmentCategory[]> {
    return apiGet<AssignmentCategory[]>('/api/assignments/categories');
  },

  async createCategory(data: { code: string; name: string; description?: string; icon?: string; color?: string }): Promise<AssignmentCategory> {
    return apiPost<AssignmentCategory>('/api/assignments/categories', data);
  },

  async updateCategory(id: number, data: { name?: string; description?: string; icon?: string; color?: string; is_active?: boolean }): Promise<AssignmentCategory> {
    return apiPut<AssignmentCategory>(`/api/assignments/categories/${id}`, data);
  },
};
